import React from 'react';
import nanoid from 'nanoid';
import { connect } from 'react-redux';
import Uppy, { UppyFile } from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';
import Compressor from '@uppy/compressor';

import InputContainer from '../../common2/InputContainer';
import Invitation from './Invitation';
import DragDrop from './DragDrop';
import ProgressIndicator from './ProgressIndicator';
import FilesList from './FilesList';
import {
  UploaderFile,
  UploaderUppyFile,
  UploaderRequestMeta,
  UploaderResponseBody,
  UploaderFilesListProps,
  isUploaderFile,
} from './types';
import withNotifications from '../../Notification/withNotifications';
import { NestedArrayError } from '../../../hooks/useNestedArrayError';

import styles from './Uploader.scss';

export type UploaderProps = {
  minFileSize?: number;
  maxNumberOfFiles: number;
  maxFileSize?: number;
  name: string;
  files?: UploaderFile[];
  errors?: Record<string, string>;
  label: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  helpText?: string;
  dropZoneText?: string;
  errorWithTranslation?: string | NestedArrayError;
  endpoint?: string;
  requestFieldName?: string;
  allowedFileTypes: Array<string>;
  onChange: (files: Array<UploaderFile>, name: string) => void;
  onBusy?: (busy: boolean) => void;
  headers?: Record<string, string>;
  filesList?: React.ComponentType<UploaderFilesListProps>;
  topRightElement?: React.ReactNode;
  isExternallyLoading?: boolean;
  externalLoadingText?: string;
};

type StateProps = {
  token: string;
  shopId: number;
};

type WithNotificationProps = {
  showError: (message: string) => void;
};

type Props = UploaderProps & StateProps & WithNotificationProps;

type State = {
  showLoader: boolean;
};

export class Uploader extends React.Component<Props, State> {
  private readonly uppy: Uppy;
  state = {
    showLoader: false,
  };

  constructor(props: Props) {
    super(props);

    const {
      maxNumberOfFiles = 1,
      maxFileSize = 10 * 1024 * 1024,
      minFileSize = 1,
      endpoint = `${process.env.TOUCHPOINTS_API_URL}/files`,
      requestFieldName = 'file',
      allowedFileTypes,
      files = [],
      headers,
    } = props;

    // eslint-disable-next-line new-cap
    this.uppy = new Uppy({
      restrictions: {
        maxNumberOfFiles,
        maxFileSize,
        allowedFileTypes,
        minFileSize,
      },
      autoProceed: true,
      id: nanoid(5),
    });

    this.uppy.use(XHRUpload, {
      endpoint,
      formData: true,
      fieldName: requestFieldName,
      headers: {
        ...headers,
        authorization: `Bearer ${props.token}`,
        'X-SHOP-ID': `${props.shopId}`,
      },
    });

    const quality = 0.6;
    this.uppy.use(Compressor, { quality });

    this.uppy.setState({ files: filesArrayToObject(files) });

    this.uppy.on('upload-success', this.onUpdateFiles);
    this.uppy.on('upload-error', this.onUploadError);
    this.uppy.on('restriction-failed', this.onRestrictionFailed);
    this.uppy.on('upload', this.onUpload);
    this.uppy.on('complete', this.onComplete);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.files !== this.props.files && this.props.files) {
      this.uppy.setState({ files: filesArrayToObject(this.props.files) });
    }
  }

  componentWillUnmount() {
    this.uppy.close();
  }

  onUploadError = (file?: UppyFile) => {
    this.props.showError('File upload error');

    if (file) {
      this.uppy.removeFile(file.id);
    }

    this.setUploaderBusy(false);
  };

  onUpload = () => {
    this.setUploaderBusy(true);
  };

  onComplete = () => {
    this.setUploaderBusy(false);
  };

  onRemove = (fileId: string) => {
    this.uppy.removeFile(fileId);
    this.onUpdateFiles();
  };

  onUpdateFiles = () => {
    const files = this.uppy.getFiles<UploaderRequestMeta, UploaderResponseBody>().map((file) => toUploaderFile(file));
    this.props.onChange(files, this.props.name);
  };

  onFileListChange = (files: UploaderFile[]) => {
    this.props.onChange(files, this.props.name);
  };

  onRestrictionFailed = (_file: UppyFile | undefined, error: Error) => {
    this.props.showError(error.message);
  };

  setUploaderBusy = (busy: boolean) => {
    this.setState({ showLoader: busy }, () => this.props.onBusy && this.props.onBusy(busy));
  };

  render() {
    const {
      label,
      name,
      isRequired,
      maxNumberOfFiles,
      allowedFileTypes,
      dropZoneText = getDefaultDropZoneText(this.props),
      helpText,
      errorWithTranslation,
      topRightElement,
      isDisabled,
      files,
      errors,
      filesList: FilesListComponent = FilesList,
      isExternallyLoading = false,
      externalLoadingText,
    } = this.props;

    const { showLoader } = this.state;

    const isLoading = isExternallyLoading || showLoader;
    const LoadingText =
      isExternallyLoading && externalLoadingText ? externalLoadingText : 'FILE_UPLOADER_UPLOADING_TEXT';

    const isMaxNumberOfFilesReached = !!files && files.length >= this.props.maxNumberOfFiles;

    // for now, we don't have cases when we need to display both field error and field items error
    // (e.g. minimum number of files and when some files miss a file type)  so we just use on or another
    const [inputError, fileListErrors] =
      typeof errorWithTranslation === 'string' ? [errorWithTranslation, undefined] : [undefined, errorWithTranslation];

    return (
      <div className={styles.container}>
        <InputContainer
          label={label}
          labelFor=""
          errorWithTranslation={inputError}
          helpText={inputError ? '' : helpText}
          labelIsRequired={isRequired}
          topRightElement={topRightElement}
        >
          <DragDrop
            uppy={this.uppy}
            inputName={name}
            isDisabled={isDisabled || isMaxNumberOfFilesReached}
            maxNumberOfFiles={maxNumberOfFiles}
            allowedFileTypes={allowedFileTypes}
          >
            {isLoading ? <ProgressIndicator>{LoadingText}</ProgressIndicator> : <Invitation>{dropZoneText}</Invitation>}
          </DragDrop>
        </InputContainer>
        <FilesListComponent files={files} errors={fileListErrors} onFilesChange={this.onFileListChange} />
      </div>
    );
  }
}

function getDefaultDropZoneText(props: UploaderProps) {
  const { maxNumberOfFiles = 1 } = props;
  return maxNumberOfFiles > 1 ? 'UPLOAD_MULTIPLE_FILES_DROP_ZONE_TEXT' : 'UPLOAD_SINGLE_FILE_DROP_ZONE_TEXT';
}

/**
 * File object that Uppy returns isn't serializable and gets modified inside store (lastModifiedDate)
 * after we open file dialog. Check https://developer.mozilla.org/en-US/docs/Web/API/File
 */
function toUploaderFile(file: UploaderFile | UploaderUppyFile): UploaderFile {
  if (isUploaderFile(file)) {
    // already serialized
    return file;
  }

  const { data, ...rest } = file;

  return rest;
}

function filesArrayToObject(files: UploaderFile[]): Record<string, UploaderFile> {
  const filesById: Record<string, UploaderFile> = {};

  files.forEach((file) => (filesById[file.id] = file));

  return filesById;
}

type ReduxState = {
  auth: {
    token: string;
  };
  user: {
    selectedShopId: number;
  };
};

function mapStateToProps(state: ReduxState): StateProps {
  const { token } = state.auth;
  const { selectedShopId } = state.user;

  return {
    token,
    shopId: selectedShopId,
  };
}

export default connect<StateProps, unknown, UploaderProps, ReduxState>(mapStateToProps)(withNotifications()(Uploader));
